import React, { useEffect, useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowUpRight, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import Logo from './Logo';
import { NAV_LINKS } from '../data/mock';

// Extracted helpers reduce the size + complexity of the main Navbar component.
function MobileNavItem({ link }) {
  if (link.children) {
    return (
      <div className="py-1">
        <div className="text-[12px] uppercase tracking-widest text-slate-500 py-2">{link.label}</div>
        {link.children.map((c) => (
          <Link key={c.to} to={c.to} className="block pl-2 py-2 text-[14px] text-slate-700 hover:text-[#E85A18]">
            {c.label}
          </Link>
        ))}
      </div>
    );
  }
  if (link.to.includes('#')) {
    return (
      <a href={link.to} className="py-2.5 text-[15px] font-medium text-slate-700 hover:text-[#E85A18]">
        {link.label}
      </a>
    );
  }
  return (
    <Link to={link.to} className="py-2.5 text-[15px] font-medium text-slate-700 hover:text-[#E85A18]">
      {link.label}
    </Link>
  );
}

function DesktopServicesMenu({ link, servicesOpen, setServicesOpen }) {
  return (
    <div
      className="relative"
      onMouseEnter={() => setServicesOpen(true)}
      onMouseLeave={() => setServicesOpen(false)}
    >
      <button className="flex items-center gap-1 px-3.5 py-2 rounded-full text-[14px] font-medium text-slate-700 hover:text-[#E85A18] hover:bg-orange-50/70 transition-colors">
        {link.label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
      </button>
      {servicesOpen && (
        <div className="absolute top-full left-0 pt-2 w-[320px]">
          <div className="rounded-2xl bg-white border border-orange-100 shadow-[0_20px_60px_-20px_rgba(232,90,24,0.35)] overflow-hidden">
            {link.children.map((c) => (
              <Link
                key={c.to}
                to={c.to}
                className="block px-5 py-4 hover:bg-[#FFF6EE] border-b last:border-b-0 border-orange-50 transition-colors"
              >
                <div className="text-[13.5px] font-semibold text-[#0F1B2D]">{c.label}</div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DesktopNavItem({ link, servicesOpen, setServicesOpen }) {
  if (link.children) {
    return <DesktopServicesMenu link={link} servicesOpen={servicesOpen} setServicesOpen={setServicesOpen} />;
  }
  const baseCls =
    'px-3.5 py-2 rounded-full text-[14px] font-medium text-slate-700 hover:text-[#E85A18] hover:bg-orange-50/70 transition-colors';
  if (link.to.includes('#')) {
    return <a href={link.to} className={baseCls}>{link.label}</a>;
  }
  return <Link to={link.to} className={baseCls}>{link.label}</Link>;
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 24);
  }, []);

  useEffect(() => {
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  const headerCls = scrolled
    ? 'bg-white/90 backdrop-blur-md border-b border-orange-100/60 shadow-[0_2px_18px_-12px_rgba(232,90,24,0.35)]'
    : 'bg-white/40 backdrop-blur-sm';

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${headerCls}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[74px] flex items-center justify-between">
        <Logo size={36} />

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <DesktopNavItem
              key={l.label}
              link={l}
              servicesOpen={servicesOpen}
              setServicesOpen={setServicesOpen}
            />
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Button
            asChild
            className="rounded-full bg-[#0F1B2D] hover:bg-[#1a2942] text-white h-10 px-5 text-[13.5px] font-medium"
          >
            <Link to="/contact">
              Request Demo <ArrowUpRight className="ml-1.5 h-4 w-4" />
            </Link>
          </Button>
        </div>

        <button
          className="lg:hidden p-2 rounded-full hover:bg-orange-50"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <div
        className={`lg:hidden overflow-hidden transition-[max-height] duration-300 bg-white/95 backdrop-blur border-t border-orange-100 ${
          open ? 'max-h-[520px]' : 'max-h-0'
        }`}
      >
        <div className="px-6 py-4 flex flex-col gap-1">
          {NAV_LINKS.map((l) => (
            <MobileNavItem key={l.label} link={l} />
          ))}
          <Button asChild className="mt-2 rounded-full bg-[#0F1B2D] hover:bg-[#1a2942] text-white">
            <Link to="/contact">Request Demo</Link>
          </Button>
        </div>
      </div>
    </header>
  );
}
